import { motion } from "framer-motion";
import { Reveal, SectionLabel, EASE } from "./Reveal";

const NODES = [
  { label: "MENU", x: "50%", y: "4%" },
  { label: "MANPOWER", x: "88%", y: "26%" },
  { label: "EQUIPMENT", x: "88%", y: "70%" },
  { label: "WORKFLOW", x: "50%", y: "92%" },
  { label: "COST", x: "12%", y: "70%" },
  { label: "SCALE", x: "12%", y: "26%" },
];

const MANIFESTO = [
  "Every menu decision affects production.",
  "Every production decision affects manpower.",
  "Every manpower decision affects cost.",
  "And every operational decision affects the ability to scale.",
];

const WhySkope = () => (
  <section id="why-skope" className="relative bg-[#f6f6f6] py-28 lg:py-40 border-y border-ink/5 overflow-hidden" data-testid="why-skope-section">
    <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 items-center">
      <div>
        <Reveal><SectionLabel>Why Skope — 04</SectionLabel></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-display font-semibold text-3xl lg:text-5xl tracking-tight text-ink leading-[1.08]" data-testid="why-skope-headline">
            THE KITCHEN IS WHERE THE BRAND COMES TO LIFE.
          </h2>
        </Reveal>
        <div className="mt-10 space-y-1">
          {MANIFESTO.map((line, i) => (
            <Reveal key={line} delay={0.1 * i} y={20}>
              <p
                className="font-display text-lg lg:text-2xl text-ink/75 tracking-tight py-1"
                data-testid={`manifesto-line-${i + 1}`}
              >
                {line}
              </p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.5}>
          <p className="mt-10 text-ink/50 leading-relaxed max-w-md">
            That is why Skope looks at the kitchen <span className="text-skope">commercially</span> — not just spatially.
            Layout follows workflow. Workflow follows the menu. And the menu follows the business.
          </p>
        </Reveal>
      </div>

      <div className="relative aspect-square max-w-[520px] mx-auto w-full" data-testid="why-skope-diagram">
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" aria-hidden="true">
          {NODES.map((n, i) => (
            <motion.line
              key={n.label}
              x1="50" y1="48" x2={parseFloat(n.x)} y2={parseFloat(n.y) + 3}
              stroke="#38B6FF" strokeOpacity="0.35" strokeWidth="0.25"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1.1, delay: 0.15 * i, ease: EASE }}
            />
          ))}
          <motion.circle
            cx="50" cy="48" r="26" fill="none" stroke="#38B6FF" strokeOpacity="0.15" strokeWidth="0.25" strokeDasharray="1 1.5"
            initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }}
            transition={{ duration: 2, ease: EASE }}
          />
        </svg>
        <motion.div
          initial={{ scale: 0.6, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="absolute left-1/2 top-[48%] -translate-x-1/2 -translate-y-1/2 w-32 h-32 lg:w-36 lg:h-36 rounded-full border border-skope bg-white flex flex-col items-center justify-center text-center shadow-[0_0_60px_rgba(56,182,255,0.15)]"
        >
          <span className="font-mono text-[9px] tracking-[0.3em] text-skope uppercase">The</span>
          <span className="font-display font-bold text-ink text-lg lg:text-xl tracking-tight">KITCHEN</span>
          <span className="font-mono text-[8px] tracking-[0.2em] text-ink/40 uppercase mt-1">Operating System</span>
        </motion.div>
        {NODES.map((n, i) => (
          <motion.div
            key={n.label}
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.15 * i + 0.4, ease: EASE }}
            className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center gap-2 bg-white border border-ink/15 px-3 py-2"
            style={{ left: n.x, top: n.y }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-skope" />
            <span className="font-mono text-[9px] lg:text-[10px] tracking-[0.2em] text-ink/70">{n.label}</span>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export default WhySkope;
